/**
 * Turns WrappedStats into the cards the recap shows (and draws into the share
 * image). A thin period simply yields fewer cards.
 */
import { WrappedStats, WrapPeriod } from './wrapped';

/** A game cover, a player's photo (initials when there is none) or a play photo. */
export interface WrapPic {
  kind: 'game' | 'player' | 'photo';
  name: string;
  url?: string;
}

/** Image URLs by game and player id (missing ones fall back to initials). */
export interface WrapImages {
  games: Record<string, string | undefined>;
  players: Record<string, string | undefined>;
}

const NO_IMAGES: WrapImages = { games: {}, players: {} };

export interface WrapCard {
  id: 'group' | 'most-played' | 'climber' | 'upset' | 'most-games' | 'me';
  label: string;
  value: string;
  detail?: string;
  /** 'up' shows the detail in green (rating gains). */
  tone?: 'up' | 'down';
  emoji: string;
  /** The card's main picture(s): one big one, or a few game covers on the group card. */
  pics: WrapPic[];
  /** Small pictures under the text: players, or the games they played. */
  row?: WrapPic[];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const signed = (n: number) => `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(1)}`;

export const buildWrapCards = (stats: WrappedStats, period: WrapPeriod, images: WrapImages = NO_IMAGES): WrapCard[] => {
  const unit = period.kind === 'year' ? 'year' : 'month';
  if (stats.playCount === 0) return [];
  const game = (gameId: string, name: string): WrapPic => ({ kind: 'game', name, url: images.games[gameId] || undefined });
  const player = (playerId: string, name: string): WrapPic => ({ kind: 'player', name, url: images.players[playerId] || undefined });
  const gameNames = new Map(stats.titles.map(t => [t.gameId, t.name]));
  const cards: WrapCard[] = [
    {
      id: 'group',
      label: 'The group played',
      value: `${plural(stats.playCount, 'game')} · ${plural(stats.titleCount, 'title')}`,
      detail: plural(stats.playerCount, 'player'),
      emoji: '🎲',
      pics: stats.titles.slice(0, 6).map(t => game(t.gameId, t.name)),
      row: stats.players.slice(0, 8).map(p => player(p.playerId, p.name)),
    },
  ];
  // "Most" needs a repeat to mean anything
  if (stats.mostPlayed && stats.mostPlayed.count > 1) {
    cards.push({
      id: 'most-played',
      label: 'Most played',
      value: stats.mostPlayed.name,
      detail: `${stats.mostPlayed.count}× on the table`,
      emoji: '🏆',
      pics: [game(stats.mostPlayed.gameId, stats.mostPlayed.name)],
    });
  }
  if (stats.biggestClimber) {
    cards.push({
      id: 'climber',
      label: 'Biggest climber',
      value: stats.biggestClimber.name,
      detail: `${signed(stats.biggestClimber.delta)} rating ▲`,
      tone: 'up',
      emoji: '📈',
      pics: [player(stats.biggestClimber.playerId, stats.biggestClimber.name)],
    });
  }
  if (stats.upset) {
    const { upset } = stats;
    const winners = upset.winnerNames.join(' & ');
    cards.push({
      id: 'upset',
      label: `Upset of the ${unit}`,
      value: `${winners} ${stats.upset.winnerNames.length > 1 ? 'win' : 'wins'}`,
      detail: `${stats.upset.gameName} (${Math.round(stats.upset.probability * 100)}% chance)`,
      emoji: '😱',
      // The play's own photo beats the box art
      pics: [upset.photoUrl ? { kind: 'photo', name: upset.gameName, url: upset.photoUrl } : game(upset.gameId, upset.gameName)],
      row: upset.winnerIds.map((id, i) => player(id, upset.winnerNames[i])),
    });
  }
  if (stats.mostGames && stats.mostGames.count > 1) {
    cards.push({
      id: 'most-games',
      label: 'Most games',
      value: stats.mostGames.name,
      detail: plural(stats.mostGames.count, 'play'),
      emoji: '🔥',
      pics: [player(stats.mostGames.playerId, stats.mostGames.name)],
      row: stats.mostGames.topGameIds.map(id => game(id, gameNames.get(id) || 'a game')),
    });
  }
  if (stats.me) {
    const { wins, plays, rankBefore, rankAfter, bestGame, delta, playerId, name } = stats.me;
    // Green when you moved up the table (or, unranked, gained rating)
    const trend = rankBefore !== null && rankAfter !== null ? rankBefore - rankAfter : rankAfter === null ? delta : 0;
    const parts: string[] = [];
    if (rankAfter !== null) {
      parts.push(rankBefore !== null && rankBefore !== rankAfter ? `Global #${rankBefore} → #${rankAfter}` : `Global #${rankAfter}`);
    } else {
      parts.push(`${signed(delta)} rating`);
    }
    if (bestGame) parts.push(`best: ${bestGame.name}`);
    cards.push({
      id: 'me',
      label: `Your ${unit}`,
      value: `${plural(wins, 'win')} in ${plural(plays, 'game')}`,
      detail: parts.join(' · '),
      tone: trend > 0 ? 'up' : trend < 0 ? 'down' : undefined,
      emoji: '🙋',
      pics: [player(playerId, name)],
      row: bestGame ? [game(bestGame.gameId, bestGame.name)] : undefined,
    });
  }
  return cards;
};
